/**
 * Cookie-backed auth storage for Supabase (Vite SPA).
 * Moves JWTs out of localStorage (XSS-readable) into cookies with Secure + SameSite.
 * Chunks values to stay under typical ~4KB cookie limits (same approach as @supabase/ssr).
 *
 * Note: browser JS cannot set HttpOnly; XSS mitigation still relies on CSP + sanitization.
 * Host-only cookies — do not set Domain=.maap.care.
 */

const CHUNK_SIZE = 3180;
const MAX_CHUNKS = 12;
const BACKUP_PREFIX = 'sb-cookie-backup:';

function isSecureContext() {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
}

function cookieAttrs() {
  const secure = isSecureContext() ? '; Secure' : '';
  return `Path=/; SameSite=Lax${secure}`;
}

function readRawCookie(name) {
  if (typeof document === 'undefined') return null;
  const encoded = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(encoded)) {
      return decodeURIComponent(part.slice(encoded.length));
    }
  }
  return null;
}

function writeRawCookie(name, value, maxAgeSec = 60 * 60 * 24 * 400) {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAgeSec}; ${cookieAttrs()}`;
}

function eraseRawCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; ${cookieAttrs()}`;
}

function backupKey(key) {
  return `${BACKUP_PREFIX}${key}`;
}

function readBackup(key) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const value = localStorage.getItem(backupKey(key));
    if (value == null) return null;
    return value;
  } catch {
    return null;
  }
}

function writeBackup(key, value) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(backupKey(key), value);
  } catch {
    /* ignore */
  }
}

function removeBackup(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(backupKey(key));
  } catch {
    /* ignore */
  }
}

function migrateLegacyLocalStorage(key) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const value = localStorage.getItem(key);
    if (value == null) return null;
    localStorage.removeItem(key);
    for (let i = 0; i < MAX_CHUNKS; i += 1) {
      localStorage.removeItem(`${key}.${i}`);
    }
    return value;
  } catch {
    return null;
  }
}

function getChunked(key) {
  const single = readRawCookie(key);
  if (single != null) return single;

  const chunks = [];
  for (let i = 0; i < MAX_CHUNKS; i += 1) {
    const part = readRawCookie(`${key}.${i}`);
    if (part == null) {
      if (i === 0) break;
      return null;
    }
    chunks.push(part);
  }
  if (chunks.length === 0) return null;
  return chunks.join('');
}

function setChunked(key, value) {
  eraseRawCookie(key);
  for (let i = 0; i < MAX_CHUNKS; i += 1) eraseRawCookie(`${key}.${i}`);

  if (value.length <= CHUNK_SIZE) {
    writeRawCookie(key, value);
    return;
  }

  const chunkCount = Math.ceil(value.length / CHUNK_SIZE);
  for (let i = 0; i < chunkCount; i += 1) {
    writeRawCookie(key + `.${i}`, value.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
  }
}

function removeChunked(key) {
  eraseRawCookie(key);
  for (let i = 0; i < MAX_CHUNKS; i += 1) eraseRawCookie(`${key}.${i}`);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(key);
      for (let i = 0; i < MAX_CHUNKS; i += 1) localStorage.removeItem(`${key}.${i}`);
    } catch {
      /* ignore */
    }
  }
}

export const supabaseAuthStorage = {
  getItem(key) {
    const fromCookie = getChunked(key);
    if (fromCookie != null) {
      writeBackup(key, fromCookie);
      return fromCookie;
    }

    const backup = readBackup(key);
    if (backup != null) {
      setChunked(key, backup);
      return backup;
    }

    const migrated = migrateLegacyLocalStorage(key);
    if (migrated != null) {
      setChunked(key, migrated);
      writeBackup(key, migrated);
      return migrated;
    }
    return null;
  },

  setItem(key, value) {
    const normalized = String(value ?? '');
    setChunked(key, normalized);
    writeBackup(key, normalized);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(key);
        for (let i = 0; i < MAX_CHUNKS; i += 1) localStorage.removeItem(`${key}.${i}`);
      } catch {
        /* ignore */
      }
    }
  },

  removeItem(key) {
    removeChunked(key);
    removeBackup(key);
  },
};
